import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { coursesApi, modulesApi } from "../lib";
import { showSuccess, showError } from "../lib/toast";
import {
  ArrowLeft,
  Save,
  Upload,
  X,
  Plus,
  Trash2,
  Image,
  ImagePlus,
  Video,
  DollarSign,
  Tag,
  Clock,
  BookOpen,
  Layout,
  CheckCircle,
  Loader,
  ChevronDown,
  ChevronUp,
  FileText,
  Code,
  HelpCircle,
  CheckSquare,
} from "lucide-react";

interface ModuleForm {
  title: string;
  description: string;
  lessons: LessonForm[];
}

interface LessonForm {
  title: string;
  type: "video" | "text" | "code" | "quiz" | "assignment" | "mixed";
  duration: number;
  isPreview: boolean;
  videoUrl: string;
  content: string;
  bullets: string;
  topics: TopicForm[];
}

interface TopicForm {
  title: string;
  description: string;
  duration: number;
  paragraphs: ParagraphForm[];
}

interface ParagraphForm {
  content: string;
}

interface FormData {
  title: string;
  shortDescription: string;
  description: string;
  category: string;
  subCategory: string;
  difficulty: string;
  language: string;
  price: number;
  originalPrice: number;
  discount: number;
  currency: string;
  thumbnailUrl: string;
  promoVideo: string;
  whatYouWillLearn: string[];
  requirements: string[];
  prerequisites: string;
  tags: string[];
  certificate: boolean;
  mobileAccess: boolean;
}

const initialFormData: FormData = {
  title: "",
  shortDescription: "",
  description: "",
  category: "",
  subCategory: "",
  difficulty: "beginner",
  language: "English",
  price: 0,
  originalPrice: 0,
  discount: 0,
  currency: "XAF",
  thumbnailUrl: "",
  promoVideo: "",
  whatYouWillLearn: [""],
  requirements: [""],
  prerequisites: "",
  tags: [],
  certificate: true,
  mobileAccess: true,
};

const categories = [
  "Web Development",
  "Mobile Development",
  "Data Science",
  "Machine Learning",
  "DevOps",
  "Design",
  "Business",
  "Marketing",
  "Photography",
  "Music",
];

const difficulties = ["beginner", "intermediate", "advanced"];
const languages = [
  "English",
  "French",
  "Spanish",
  "German",
  "Chinese",
  "Portuguese",
];

export default function CreateCoursePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [tagInput, setTagInput] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [modules, setModules] = useState<ModuleForm[]>([]);
  const [expandedModule, setExpandedModule] = useState<number | null>(null);

  const addModule = () => {
    setModules([...modules, { title: "", description: "", lessons: [] }]);
    setExpandedModule(modules.length);
  };

  const updateModule = (index: number, field: keyof ModuleForm, value: any) => {
    const updated = [...modules];
    (updated[index] as any)[field] = value;
    setModules(updated);
  };

  const removeModule = (index: number) => {
    setModules(modules.filter((_, i) => i !== index));
    if (expandedModule === index) setExpandedModule(null);
  };

  const addLesson = (moduleIndex: number) => {
    const updated = [...modules];
    updated[moduleIndex].lessons.push({
      title: "",
      type: "video",
      duration: 0,
      isPreview: false,
      videoUrl: "",
      content: "",
      bullets: "",
      topics: [],
    });
    setModules(updated);
  };

  const addTopic = (moduleIndex: number, lessonIndex: number) => {
    const updated = [...modules];
    updated[moduleIndex].lessons[lessonIndex].topics.push({
      title: "",
      description: "",
      duration: 0,
      paragraphs: [],
    });
    setModules(updated);
  };

  const updateTopic = (
    moduleIndex: number,
    lessonIndex: number,
    topicIndex: number,
    field: keyof TopicForm,
    value: any,
  ) => {
    const updated = [...modules];
    (updated[moduleIndex].lessons[lessonIndex].topics[topicIndex] as any)[
      field
    ] = value;
    setModules(updated);
  };

  const removeTopic = (
    moduleIndex: number,
    lessonIndex: number,
    topicIndex: number,
  ) => {
    const updated = [...modules];
    updated[moduleIndex].lessons[lessonIndex].topics = updated[
      moduleIndex
    ].lessons[lessonIndex].topics.filter((_, i) => i !== topicIndex);
    setModules(updated);
  };

  const addParagraph = (
    moduleIndex: number,
    lessonIndex: number,
    topicIndex: number,
  ) => {
    const updated = [...modules];
    updated[moduleIndex].lessons[lessonIndex].topics[
      topicIndex
    ].paragraphs.push({
      content: "",
    });
    setModules(updated);
  };

  const updateParagraph = (
    moduleIndex: number,
    lessonIndex: number,
    topicIndex: number,
    paraIndex: number,
    value: string,
  ) => {
    const updated = [...modules];
    updated[moduleIndex].lessons[lessonIndex].topics[topicIndex].paragraphs[
      paraIndex
    ].content = value;
    setModules(updated);
  };

  const removeParagraph = (
    moduleIndex: number,
    lessonIndex: number,
    topicIndex: number,
    paraIndex: number,
  ) => {
    const updated = [...modules];
    updated[moduleIndex].lessons[lessonIndex].topics[topicIndex].paragraphs =
      updated[moduleIndex].lessons[lessonIndex].topics[
        topicIndex
      ].paragraphs.filter((_, i) => i !== paraIndex);
    setModules(updated);
  };

  const updateLesson = (
    moduleIndex: number,
    lessonIndex: number,
    field: keyof LessonForm,
    value: any,
  ) => {
    const updated = [...modules];
    (updated[moduleIndex].lessons[lessonIndex] as any)[field] = value;
    setModules(updated);
  };

  const removeLesson = (moduleIndex: number, lessonIndex: number) => {
    const updated = [...modules];
    updated[moduleIndex].lessons = updated[moduleIndex].lessons.filter(
      (_, i) => i !== lessonIndex,
    );
    setModules(updated);
  };

  const getLessonIcon = (type: string) => {
    switch (type) {
      case "video":
        return <Video className="w-4 h-4 text-blue-500" />;
      case "text":
        return <FileText className="w-4 h-4 text-amber-500" />;
      case "code":
        return <Code className="w-4 h-4 text-violet-500" />;
      case "mixed":
        return <Layout className="w-4 h-4 text-orange-500" />;
      case "quiz":
        return <HelpCircle className="w-4 h-4 text-rose-500" />;
      case "assignment":
        return <CheckSquare className="w-4 h-4 text-teal-500" />;
      default:
        return <BookOpen className="w-4 h-4 text-gray-400" />;
    }
  };

  const updateField = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleThumbnailUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showError("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showError("Image must be less than 5MB");
      return;
    }

    setUploadingThumbnail(true);
    try {
      const response = await coursesApi.uploadThumbnail(file);
      if (response.success && response.data?.url) {
        updateField("thumbnailUrl", response.data.url);
        showSuccess("Thumbnail uploaded successfully");
      }
    } catch (err: any) {
      showError(err.message || "Failed to upload thumbnail");
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleThumbnailUrlChange = (url: string) => {
    updateField("thumbnailUrl", url);
  };

  const handleArrayField = (
    field: "whatYouWillLearn" | "requirements",
    index: number,
    value: string,
  ) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    updateField(field, newArray);
  };

  const addArrayItem = (field: "whatYouWillLearn" | "requirements") => {
    updateField(field, [...formData[field], ""]);
  };

  const removeArrayItem = (
    field: "whatYouWillLearn" | "requirements",
    index: number,
  ) => {
    if (formData[field].length > 1) {
      const newArray = formData[field].filter((_, i) => i !== index);
      updateField(field, newArray);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      updateField("tags", [...formData.tags, tagInput.trim()]);
      setTagInput("");
    }
  };

  const removeTag = (tag: string) => {
    updateField(
      "tags",
      formData.tags.filter((t) => t !== tag),
    );
  };

  const handleSubmit = async (publish: boolean = false) => {
    if (!formData.title.trim()) {
      showError("Course title is required");
      return;
    }
    if (!formData.shortDescription.trim()) {
      showError("Short description is required");
      return;
    }
    if (!formData.description.trim()) {
      showError("Course description is required");
      return;
    }
    if (!formData.category) {
      showError("Please select a category");
      return;
    }

    setLoading(true);
    try {
      const courseData = {
        ...formData,
        whatYouWillLearn: formData.whatYouWillLearn
          .filter((item) => item.trim())
          .join("\n"),
        requirements: formData.requirements
          .filter((item) => item.trim())
          .join("\n"),
        isPublished: publish,
      };

      const response = await coursesApi.createCourse(courseData);

      if (response.success) {
        const courseId = response.data._id;

        if (modules.length > 0) {
          for (let i = 0; i < modules.length; i++) {
            const moduleData = modules[i];
            if (!moduleData.title.trim()) continue;

            const moduleRes = await modulesApi.createModule(courseId, {
              title: moduleData.title,
              description: moduleData.description,
            });

            if (moduleRes.success && moduleData.lessons.length > 0) {
              const moduleId = moduleRes.data._id;

              for (const lesson of moduleData.lessons) {
                if (!lesson.title.trim()) continue;

                const lessonData: any = {
                  title: lesson.title,
                  type: lesson.type,
                  duration: lesson.duration,
                  isPreview: lesson.isPreview,
                  isPublished: true,
                  videoUrl: lesson.videoUrl,
                  content: lesson.content,
                  bullets: lesson.bullets,
                };

                if (
                  (lesson.type === "text" || lesson.type === "mixed") &&
                  lesson.topics &&
                  lesson.topics.length > 0
                ) {
                  lessonData.topics = lesson.topics.map((topic) => ({
                    title: topic.title,
                    description: topic.description,
                    duration: topic.duration,
                    paragraphs:
                      topic.paragraphs?.map((p) => ({
                        content: p.content,
                      })) || [],
                  }));
                  lessonData.paragraphs = lesson.topics.flatMap(
                    (topic) =>
                      topic.paragraphs?.map((p) => ({ content: p.content })) ||
                      [],
                  );
                }

                await modulesApi.createLesson(moduleId, lessonData);
              }
            }
          }
        }

        showSuccess(
          publish ? "Course published successfully!" : "Course saved as draft!",
        );
        navigate(`/dashboard/instructor/courses/${courseId}`);
      }
    } catch (err: any) {
      showError(err.message || "Failed to create course");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-4 pb-10 bg-gradient-to-br from-gray-50 to-gray-100 md:py-6">
      <div className="px-4 mx-auto max-w-7xl md:px-6 lg:px-8">
        <div className="flex flex-col gap-3 mb-5 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/dashboard/instructor")}
              className="p-1.5 rounded-lg hover:bg-gray-100 md:p-2"
            >
              <ArrowLeft className="w-4 h-4 text-gray-600 md:w-5 md:h-5" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-black md:text-xl">
                Create New Course
              </h1>
              <p className="text-[10px] text-gray-500 md:text-xs">
                Fill in the details to create your course
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => handleSubmit(false)}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] md:text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <Save className="w-3.5 h-3.5 md:w-4 md:h-4" />
              Save Draft
            </button>
            <button
              onClick={() => handleSubmit(true)}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] md:text-xs text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md disabled:opacity-50"
            >
              <CheckCircle className="w-3.5 h-3.5 md:w-4 md:h-4" />
              {loading ? "Publishing..." : "Publish Course"}
            </button>
          </div>
        </div>

        <div className="space-y-4 md:space-y-6">
          {/* Basic Info */}
          <div className="p-4 bg-white border border-gray-100 rounded-xl shadow-sm md:p-6">
            <h2 className="flex items-center gap-2 mb-3 text-sm font-bold text-black md:mb-4">
              <BookOpen className="w-4 h-4 text-orange-500" />
              Basic Information
            </h2>
            <div className="space-y-3 md:space-y-4">
              <div>
                <label className="block mb-1 text-[11px] md:text-sm font-medium text-gray-700">
                  Course Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => updateField("title", e.target.value)}
                  placeholder="e.g., Complete Web Development Bootcamp 2024"
                  className="w-full px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                />
              </div>

              <div>
                <label className="block mb-1 text-[11px] md:text-sm font-medium text-gray-700">
                  Short Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.shortDescription}
                  onChange={(e) =>
                    updateField("shortDescription", e.target.value)
                  }
                  placeholder="A brief summary of your course (max 200 characters)"
                  rows={2}
                  maxLength={200}
                  className="w-full px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                />
                <p className="mt-1 text-[9px] md:text-xs text-gray-400">
                  {formData.shortDescription.length}/200 characters
                </p>
              </div>

              <div>
                <label className="block mb-1 text-[11px] md:text-sm font-medium text-gray-700">
                  Full Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => updateField("description", e.target.value)}
                  placeholder="Detailed description of your course"
                  rows={5}
                  className="w-full px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="block mb-1 text-[11px] md:text-sm font-medium text-gray-700">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => updateField("category", e.target.value)}
                    className="w-full px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                  >
                    <option value="">Select category</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block mb-1 text-[11px] md:text-sm font-medium text-gray-700">
                    Sub Category
                  </label>
                  <input
                    type="text"
                    value={formData.subCategory}
                    onChange={(e) => updateField("subCategory", e.target.value)}
                    placeholder="e.g., Full Stack"
                    className="w-full px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div>
                  <label className="block mb-1 text-[11px] md:text-sm font-medium text-gray-700">
                    Difficulty
                  </label>
                  <select
                    value={formData.difficulty}
                    onChange={(e) => updateField("difficulty", e.target.value)}
                    className="w-full px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                  >
                    {difficulties.map((diff) => (
                      <option key={diff} value={diff}>
                        {diff.charAt(0).toUpperCase() + diff.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block mb-1 text-[11px] md:text-sm font-medium text-gray-700">
                    Language
                  </label>
                  <select
                    value={formData.language}
                    onChange={(e) => updateField("language", e.target.value)}
                    className="w-full px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                  >
                    {languages.map((lang) => (
                      <option key={lang} value={lang}>
                        {lang}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block mb-1 text-[11px] md:text-sm font-medium text-gray-700">
                    Currency
                  </label>
                  <select
                    value={formData.currency}
                    onChange={(e) => updateField("currency", e.target.value)}
                    className="w-full px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                  >
                    <option value="XAF">XAF</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="p-4 bg-white border border-gray-100 rounded-xl shadow-sm md:p-6">
            <h2 className="flex items-center gap-2 mb-3 text-sm font-bold text-black md:mb-4">
              <DollarSign className="w-4 h-4 text-orange-500" />
              Pricing
            </h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <label className="block mb-1 text-[11px] md:text-sm font-medium text-gray-700">
                  Price
                </label>
                <input
                  type="number"
                  value={formData.price}
                  onChange={(e) => updateField("price", Number(e.target.value))}
                  placeholder="0"
                  min="0"
                  className="w-full px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                />
              </div>
              <div>
                <label className="block mb-1 text-[11px] md:text-sm font-medium text-gray-700">
                  Original Price
                </label>
                <input
                  type="number"
                  value={formData.originalPrice}
                  onChange={(e) =>
                    updateField("originalPrice", Number(e.target.value))
                  }
                  placeholder="0"
                  min="0"
                  className="w-full px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                />
              </div>
              <div>
                <label className="block mb-1 text-[11px] md:text-sm font-medium text-gray-700">
                  Discount (%)
                </label>
                <input
                  type="number"
                  value={formData.discount}
                  onChange={(e) =>
                    updateField("discount", Number(e.target.value))
                  }
                  placeholder="0"
                  min="0"
                  max="100"
                  className="w-full px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                />
              </div>
            </div>
            <p className="mt-2 text-[9px] md:text-xs text-gray-500">
              Set price to 0 for a free course
            </p>
          </div>

          {/* Media - Fixed Thumbnail Section */}
          <div className="p-4 bg-white border border-gray-100 rounded-xl shadow-sm md:p-6">
            <h2 className="flex items-center gap-2 mb-3 text-sm font-bold text-black md:mb-4">
              <Image className="w-4 h-4 text-orange-500" />
              Media
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block mb-2 text-[11px] md:text-sm font-medium text-gray-700">
                  Course Thumbnail
                </label>

                {/* Mobile: Stack vertically, Desktop: Row layout */}
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:flex-wrap">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleThumbnailUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingThumbnail}
                    className="flex items-center justify-center gap-2 px-4 py-2 text-xs text-white rounded-lg whitespace-nowrap bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md disabled:opacity-50 sm:text-sm"
                  >
                    {uploadingThumbnail ? (
                      <Loader className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <ImagePlus className="w-3.5 h-3.5" />
                    )}
                    Upload from Device
                  </button>
                  <span className="text-[10px] text-gray-400 sm:text-xs">
                    or
                  </span>
                  <input
                    type="url"
                    value={formData.thumbnailUrl}
                    onChange={(e) => handleThumbnailUrlChange(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                    className="flex-1 px-3 py-2 text-[11px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 sm:text-sm"
                  />
                </div>

                {/* Preview */}
                {formData.thumbnailUrl && (
                  <div className="mt-3 w-40 overflow-hidden bg-gray-100 border border-gray-200 rounded-lg h-24 sm:w-48 sm:h-28">
                    <img
                      src={formData.thumbnailUrl}
                      alt="Thumbnail preview"
                      className="object-cover w-full h-full"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  </div>
                )}
                <p className="mt-1 text-[9px] text-gray-400 sm:text-xs">
                  Recommended size: 1280x720px. Max file size: 5MB
                </p>
              </div>

              <div>
                <label className="block mb-1 text-[11px] md:text-sm font-medium text-gray-700">
                  Promo Video URL
                </label>
                <input
                  type="url"
                  value={formData.promoVideo}
                  onChange={(e) => updateField("promoVideo", e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="w-full px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                />
              </div>
            </div>
          </div>

          {/* What you'll learn */}
          <div className="p-4 bg-white border border-gray-100 rounded-xl shadow-sm md:p-6">
            <h2 className="flex items-center gap-2 mb-3 text-sm font-bold text-black md:mb-4">
              <CheckCircle className="w-4 h-4 text-orange-500" />
              What You'll Learn
            </h2>
            <div className="space-y-2">
              {formData.whatYouWillLearn.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) =>
                      handleArrayField(
                        "whatYouWillLearn",
                        index,
                        e.target.value,
                      )
                    }
                    placeholder={`Learning point ${index + 1}`}
                    className="flex-1 px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                  />
                  {formData.whatYouWillLearn.length > 1 && (
                    <button
                      onClick={() => removeArrayItem("whatYouWillLearn", index)}
                      className="p-2 text-red-500 rounded-lg hover:bg-red-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => addArrayItem("whatYouWillLearn")}
                className="flex items-center gap-1 text-[11px] md:text-sm text-orange-600 hover:underline"
              >
                <Plus className="w-3.5 h-3.5" /> Add more
              </button>
            </div>
          </div>

          {/* Requirements */}
          <div className="p-4 bg-white border border-gray-100 rounded-xl shadow-sm md:p-6">
            <h2 className="flex items-center gap-2 mb-3 text-sm font-bold text-black md:mb-4">
              <Layout className="w-4 h-4 text-orange-500" />
              Requirements
            </h2>
            <div className="space-y-2">
              {formData.requirements.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) =>
                      handleArrayField("requirements", index, e.target.value)
                    }
                    placeholder={`Requirement ${index + 1}`}
                    className="flex-1 px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                  />
                  {formData.requirements.length > 1 && (
                    <button
                      onClick={() => removeArrayItem("requirements", index)}
                      className="p-2 text-red-500 rounded-lg hover:bg-red-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => addArrayItem("requirements")}
                className="flex items-center gap-1 text-[11px] md:text-sm text-orange-600 hover:underline"
              >
                <Plus className="w-3.5 h-3.5" /> Add more
              </button>
            </div>
          </div>

          {/* Additional Info */}
          <div className="p-4 bg-white border border-gray-100 rounded-xl shadow-sm md:p-6">
            <h2 className="flex items-center gap-2 mb-3 text-sm font-bold text-black md:mb-4">
              <Tag className="w-4 h-4 text-orange-500" />
              Additional Information
            </h2>
            <div className="space-y-3 md:space-y-4">
              <div>
                <label className="block mb-1 text-[11px] md:text-sm font-medium text-gray-700">
                  Prerequisites
                </label>
                <textarea
                  value={formData.prerequisites}
                  onChange={(e) => updateField("prerequisites", e.target.value)}
                  placeholder="What students should know before taking this course"
                  rows={2}
                  className="w-full px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                />
              </div>

              <div>
                <label className="block mb-1 text-[11px] md:text-sm font-medium text-gray-700">
                  Tags
                </label>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && addTag()}
                    placeholder="Add a tag"
                    className="flex-1 px-3 py-2 text-[11px] md:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400"
                  />
                  <button
                    onClick={addTag}
                    type="button"
                    className="px-4 py-2 text-[11px] md:text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.tags.map((tag) => (
                    <span
                      key={tag}
                      className="flex items-center gap-1 px-2 py-1 text-[9px] md:text-xs rounded-full bg-orange-100 text-orange-700"
                    >
                      {tag}
                      <button
                        onClick={() => removeTag(tag)}
                        className="hover:text-red-500"
                      >
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.certificate}
                    onChange={(e) =>
                      updateField("certificate", e.target.checked)
                    }
                    className="w-3.5 h-3.5 border-gray-300 rounded text-orange-500 focus:ring-orange-500"
                  />
                  <span className="text-[11px] md:text-sm text-gray-600">
                    Certificate of completion
                  </span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.mobileAccess}
                    onChange={(e) =>
                      updateField("mobileAccess", e.target.checked)
                    }
                    className="w-3.5 h-3.5 border-gray-300 rounded text-orange-500 focus:ring-orange-500"
                  />
                  <span className="text-[11px] md:text-sm text-gray-600">
                    Mobile access
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Modules & Lessons Section */}
          <div className="p-4 bg-white border border-gray-100 rounded-xl shadow-sm md:p-6">
            <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="flex items-center gap-2 text-sm font-bold text-black">
                <Layout className="w-4 h-4 text-orange-500" />
                Course Curriculum
              </h2>
              <button
                onClick={addModule}
                className="flex items-center justify-center gap-1 text-[11px] md:text-sm text-orange-600 hover:underline"
              >
                <Plus className="w-3.5 h-3.5" /> Add Module
              </button>
            </div>

            {modules.length === 0 ? (
              <p className="py-4 text-[11px] text-center text-gray-500 md:text-sm">
                No modules added yet. Add modules to structure your course
                content.
              </p>
            ) : (
              <div className="space-y-3 md:space-y-4">
                {modules.map((module, mIndex) => (
                  <div
                    key={mIndex}
                    className="overflow-hidden border border-gray-200 rounded-lg"
                  >
                    <div
                      className="flex flex-wrap items-center gap-2 p-3 bg-gray-50 cursor-pointer sm:p-4 sm:gap-4"
                      onClick={() =>
                        setExpandedModule(
                          expandedModule === mIndex ? null : mIndex,
                        )
                      }
                    >
                      <div className="flex items-center justify-center w-8 h-8 text-sm font-bold text-white rounded-lg shrink-0 bg-gradient-to-r from-orange-600 to-orange-500 sm:w-10 sm:h-10">
                        {mIndex + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <input
                          type="text"
                          value={module.title}
                          onChange={(e) =>
                            updateModule(mIndex, "title", e.target.value)
                          }
                          placeholder="Module title"
                          className="w-full text-xs font-semibold bg-transparent border-none focus:outline-none sm:text-base"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeModule(mIndex);
                        }}
                        className="p-1.5 text-gray-400 rounded-lg hover:text-red-500 hover:bg-red-50 sm:p-2"
                      >
                        <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                      </button>
                      {expandedModule === mIndex ? (
                        <ChevronUp className="w-4 h-4 text-gray-400 sm:w-5 sm:h-5" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-400 sm:w-5 sm:h-5" />
                      )}
                    </div>

                    {expandedModule === mIndex && (
                      <div className="p-3 bg-white border-t border-gray-200 sm:p-5">
                        <div className="mb-4 sm:mb-5">
                          <label className="block text-[11px] sm:text-sm font-medium text-gray-700 mb-1.5">
                            Description
                          </label>
                          <textarea
                            value={module.description}
                            onChange={(e) =>
                              updateModule(
                                mIndex,
                                "description",
                                e.target.value,
                              )
                            }
                            placeholder="What will students learn in this module?"
                            rows={3}
                            className="w-full px-3 py-2 text-[11px] sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-400 resize-none"
                          />
                        </div>

                        <div className="space-y-3">
                          {module.lessons.map((lesson, lIndex) => (
                            <div
                              key={lIndex}
                              className="overflow-hidden border border-gray-200 rounded-lg"
                            >
                              <div className="flex flex-wrap items-center gap-2 p-3 border-b border-gray-200 bg-gray-50 sm:p-4 sm:gap-4">
                                {getLessonIcon(lesson.type)}
                                <div className="flex-1 min-w-0">
                                  <input
                                    type="text"
                                    value={lesson.title}
                                    onChange={(e) =>
                                      updateLesson(
                                        mIndex,
                                        lIndex,
                                        "title",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Lesson title"
                                    className="w-full text-[11px] font-medium bg-transparent border-none focus:outline-none sm:text-base"
                                  />
                                </div>
                                <div className="flex items-center gap-2">
                                  <label className="flex items-center gap-1 text-[10px] sm:text-sm text-gray-600">
                                    <input
                                      type="checkbox"
                                      checked={lesson.isPreview}
                                      onChange={(e) =>
                                        updateLesson(
                                          mIndex,
                                          lIndex,
                                          "isPreview",
                                          e.target.checked,
                                        )
                                      }
                                      className="w-3 h-3 border-gray-300 rounded text-orange-500 focus:ring-orange-500 sm:w-4 sm:h-4"
                                    />
                                    <span className="hidden xs:inline">
                                      Preview
                                    </span>
                                  </label>
                                  <button
                                    onClick={() => removeLesson(mIndex, lIndex)}
                                    className="p-1 text-gray-400 rounded-lg hover:text-red-500 hover:bg-red-50 sm:p-2"
                                  >
                                    <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                  </button>
                                </div>
                              </div>

                              <div className="grid grid-cols-1 gap-3 p-3 bg-white sm:grid-cols-2 sm:p-4">
                                <div>
                                  <label className="block text-[10px] sm:text-sm font-medium text-gray-700 mb-1">
                                    Type
                                  </label>
                                  <select
                                    value={lesson.type}
                                    onChange={(e) =>
                                      updateLesson(
                                        mIndex,
                                        lIndex,
                                        "type",
                                        e.target.value,
                                      )
                                    }
                                    className="w-full px-2 py-1.5 text-[10px] sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-400"
                                  >
                                    <option value="video">Video</option>
                                    <option value="text">Text</option>
                                    <option value="code">Code</option>
                                    <option value="mixed">Mixed</option>
                                    <option value="quiz">Quiz</option>
                                    <option value="assignment">
                                      Assignment
                                    </option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-[10px] sm:text-sm font-medium text-gray-700 mb-1">
                                    Duration (min)
                                  </label>
                                  <input
                                    type="number"
                                    value={lesson.duration}
                                    onChange={(e) =>
                                      updateLesson(
                                        mIndex,
                                        lIndex,
                                        "duration",
                                        parseInt(e.target.value) || 0,
                                      )
                                    }
                                    min="0"
                                    className="w-full px-2 py-1.5 text-[10px] sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-400"
                                  />
                                </div>

                                {(lesson.type === "video" ||
                                  lesson.type === "mixed") && (
                                  <div className="sm:col-span-2">
                                    <label className="block text-[10px] sm:text-sm font-medium text-gray-700 mb-1">
                                      Video URL
                                    </label>
                                    <input
                                      type="url"
                                      value={lesson.videoUrl}
                                      onChange={(e) =>
                                        updateLesson(
                                          mIndex,
                                          lIndex,
                                          "videoUrl",
                                          e.target.value,
                                        )
                                      }
                                      placeholder="https://youtube.com/embed/..."
                                      className="w-full px-2 py-1.5 text-[10px] sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-400"
                                    />
                                  </div>
                                )}

                                {(lesson.type === "text" ||
                                  lesson.type === "mixed") && (
                                  <div className="sm:col-span-2">
                                    <label className="block text-[10px] sm:text-sm font-medium text-gray-700 mb-1">
                                      Content
                                    </label>
                                    <textarea
                                      value={lesson.content}
                                      onChange={(e) =>
                                        updateLesson(
                                          mIndex,
                                          lIndex,
                                          "content",
                                          e.target.value,
                                        )
                                      }
                                      placeholder="Lesson content..."
                                      rows={4}
                                      className="w-full px-2 py-1.5 text-[10px] sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-400 resize-none"
                                    />
                                  </div>
                                )}
                              </div>

                              {/* Topics and Paragraphs */}
                              {(lesson.type === "text" ||
                                lesson.type === "mixed") && (
                                <div className="px-3 pt-2 pb-3 space-y-3 border-t border-gray-100 bg-gray-50 sm:px-4 sm:pt-3 sm:pb-4">
                                  <div className="flex flex-wrap items-center justify-between gap-2">
                                    <label className="text-[10px] sm:text-sm font-medium text-gray-700">
                                      Topics
                                    </label>
                                    <button
                                      type="button"
                                      onClick={() => addTopic(mIndex, lIndex)}
                                      className="text-[10px] sm:text-sm font-medium text-orange-600 hover:underline"
                                    >
                                      + Add Topic
                                    </button>
                                  </div>

                                  {lesson.topics.map((topic, tIndex) => (
                                    <div
                                      key={tIndex}
                                      className="p-3 bg-white border border-gray-200 rounded-lg sm:p-4"
                                    >
                                      <div className="flex flex-wrap items-center gap-2 mb-2 sm:gap-3">
                                        <input
                                          type="text"
                                          value={topic.title}
                                          onChange={(e) =>
                                            updateTopic(
                                              mIndex,
                                              lIndex,
                                              tIndex,
                                              "title",
                                              e.target.value,
                                            )
                                          }
                                          placeholder="Topic title"
                                          className="flex-1 px-2 py-1.5 text-[10px] sm:text-sm border border-gray-300 rounded-lg"
                                        />
                                        <input
                                          type="number"
                                          value={topic.duration}
                                          onChange={(e) =>
                                            updateTopic(
                                              mIndex,
                                              lIndex,
                                              tIndex,
                                              "duration",
                                              parseInt(e.target.value) || 0,
                                            )
                                          }
                                          placeholder="Min"
                                          className="w-16 px-2 py-1.5 text-[10px] sm:text-sm border border-gray-300 rounded-lg"
                                        />
                                        <button
                                          type="button"
                                          onClick={() =>
                                            removeTopic(mIndex, lIndex, tIndex)
                                          }
                                          className="p-1 text-gray-400 rounded-lg hover:text-red-500 hover:bg-red-50"
                                        >
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                      </div>

                                      <div className="mb-2">
                                        <input
                                          type="text"
                                          value={topic.description}
                                          onChange={(e) =>
                                            updateTopic(
                                              mIndex,
                                              lIndex,
                                              tIndex,
                                              "description",
                                              e.target.value,
                                            )
                                          }
                                          placeholder="Topic description (optional)"
                                          className="w-full px-2 py-1.5 text-[10px] sm:text-sm border border-gray-300 rounded-lg"
                                        />
                                      </div>

                                      <div className="pl-2 space-y-2 border-gray-300 border-l-2 sm:pl-4 sm:space-y-3">
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                          <label className="text-[9px] sm:text-xs font-medium text-gray-600">
                                            Paragraphs
                                          </label>
                                          <button
                                            type="button"
                                            onClick={() =>
                                              addParagraph(
                                                mIndex,
                                                lIndex,
                                                tIndex,
                                              )
                                            }
                                            className="text-[9px] sm:text-xs font-medium text-gray-500 hover:text-orange-600"
                                          >
                                            + Add Paragraph
                                          </button>
                                        </div>
                                        {topic.paragraphs.map(
                                          (para, pIndex) => (
                                            <div
                                              key={pIndex}
                                              className="flex items-start gap-2"
                                            >
                                              <textarea
                                                value={para.content}
                                                onChange={(e) =>
                                                  updateParagraph(
                                                    mIndex,
                                                    lIndex,
                                                    tIndex,
                                                    pIndex,
                                                    e.target.value,
                                                  )
                                                }
                                                placeholder="Paragraph content..."
                                                rows={2}
                                                className="flex-1 px-2 py-1.5 text-[10px] sm:text-sm border border-gray-300 rounded-lg resize-none"
                                              />
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  removeParagraph(
                                                    mIndex,
                                                    lIndex,
                                                    tIndex,
                                                    pIndex,
                                                  )
                                                }
                                                className="p-1 text-gray-400 rounded-lg hover:text-red-500 hover:bg-red-50 shrink-0"
                                              >
                                                <X className="w-3 h-3" />
                                              </button>
                                            </div>
                                          ),
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {(lesson.type === "video" ||
                                lesson.type === "mixed") && (
                                <div className="px-3 pb-3 sm:px-4 sm:pb-4">
                                  <label className="block text-[10px] sm:text-sm font-medium text-gray-700 mb-1">
                                    Bullet Points (one per line)
                                  </label>
                                  <textarea
                                    value={lesson.bullets}
                                    onChange={(e) =>
                                      updateLesson(
                                        mIndex,
                                        lIndex,
                                        "bullets",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Point 1&#10;Point 2&#10;Point 3"
                                    rows={3}
                                    className="w-full px-2 py-1.5 text-[10px] sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-400 resize-none"
                                  />
                                </div>
                              )}
                            </div>
                          ))}
                          <button
                            onClick={() => addLesson(mIndex)}
                            className="w-full py-2 text-[11px] font-medium text-gray-500 border-2 border-gray-200 border-dashed rounded-lg hover:text-orange-600 hover:bg-gray-50 hover:border-orange-300 sm:py-3 sm:text-sm"
                          >
                            + Add Lesson
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
